import { FC, SVGProps } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { topLeftToBottomRightPatternMask } from '@/shared/styles';

const FIELD_COLUMNS = 156;
const FIELD_ROWS = 118;
const FIELD_WIDTH = 1_200;
const FIELD_HEIGHT = 1_000;

interface ProjectedDot {
    radius: number;
    x: number;
    y: number;
}

type PlatformDotWaveProps = Omit<SVGProps<SVGSVGElement>, 'children'>;

const gaussian = (
    x: number,
    depth: number,
    centerX: number,
    centerDepth: number,
    spreadX: number,
    spreadDepth: number,
) => Math.exp(-Math.pow((x - centerX) / spreadX, 2) - Math.pow((depth - centerDepth) / spreadDepth, 2));

const projectedDots: ProjectedDot[] = Array.from({ length: FIELD_ROWS }, (_, row) => {
    const depth = row / (FIELD_ROWS - 1);
    const perspective = 1 - depth * 0.28;
    const projectedBaseY = FIELD_HEIGHT + 60 - Math.pow(depth, 0.9) * (FIELD_HEIGHT + 160);

    return Array.from({ length: FIELD_COLUMNS }, (_, column) => {
        const normalizedX = (column / (FIELD_COLUMNS - 1)) * 2.5 - 1.25;
        const rollingWave = Math.sin((normalizedX * 1.65 + depth * 1.15) * Math.PI) * 0.16;
        const crossingWave = Math.cos((normalizedX * 0.9 - depth * 2.15) * Math.PI) * 0.1;
        const nearCrest = gaussian(normalizedX, depth, -0.76, 0.2, 0.3, 0.36) * 0.82;
        const farCrest = gaussian(normalizedX, depth, 0.58, 0.78, 0.24, 0.3) * 0.72;
        const centerTrough = gaussian(normalizedX, depth, 0.02, 0.47, 0.48, 0.38) * -0.32;
        const elevation = rollingWave + crossingWave + nearCrest + farCrest + centerTrough;
        const worldX = normalizedX * FIELD_WIDTH * 0.7;
        const x = FIELD_WIDTH / 2 + worldX * perspective + depth * 54 + elevation * 28;
        const y = projectedBaseY - elevation * 180 * perspective + normalizedX * 34;

        return {
            radius: 0.62 + (1 - depth) * 0.72 + Math.max(0, elevation) * 0.14,
            x,
            y,
        };
    });
}).flat();

const projectedDotPath = projectedDots
    .map(({ radius, x, y }) => {
        const diameter = radius * 2;

        return [
            `M ${(x - radius).toFixed(2)} ${y.toFixed(2)}`,
            `a ${radius.toFixed(2)} ${radius.toFixed(2)} 0 1 0 ${diameter.toFixed(2)} 0`,
            `a ${radius.toFixed(2)} ${radius.toFixed(2)} 0 1 0 -${diameter.toFixed(2)} 0`,
        ].join(' ');
    })
    .join(' ');

const PlatformDotWave: FC<PlatformDotWaveProps> = ({ style, ...svgProps }): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <svg
            {...svgProps}
            aria-hidden
            focusable='false'
            preserveAspectRatio='xMidYMid slice'
            style={{
                inset: 0,
                maskImage: topLeftToBottomRightPatternMask,
                pointerEvents: 'none',
                position: 'absolute',
                WebkitMaskImage: topLeftToBottomRightPatternMask,
                ...style,
            }}
            viewBox={`0 0 ${FIELD_WIDTH} ${FIELD_HEIGHT}`}
            width='100%'
            height='100%'
        >
            <path
                d={projectedDotPath}
                fill={token.colorTextQuaternary}
                opacity='0.6'
                stroke={token.colorTextQuaternary}
                strokeWidth='0.25'
            />
        </svg>
    );
};

export default PlatformDotWave;
